/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Label } from '@fluentui/react/lib/Label';
import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { multiLingualLearnMore } from 'src/clu/cluExternalLinks';
import { CluModalScreen, CluProjectCreationSteps, CluProjectDto } from 'src/clu/verticals/cluProjectCreationModal/CluProjectCreationModal';
import { CluModalFooter } from 'src/clu/verticals/cluProjectCreationModal/components/CluModalFooter';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown, LuisDropdownProps } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField, LuisTextFieldProps } from 'src/core/components/LuisTextField';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { FormState } from 'src/core/types/form';
import { globalMessages } from 'src/core/utils/globalMessages';

export type CluBasicInfoScreenProps = {
    onNext: () => void;
    onDismiss: () => void;
    projectDto: FormState<CluProjectDto>;
    onUpdate: (dto: Partial<CluProjectDto>) => void;
};

const messages = defineMessages({
    nameLabel: { id: 'clu.cluBasicInfoScreen.nameLabel', defaultMessage: 'Name' },
    title: { id: 'clu.cluBasicInfoScreen.title', defaultMessage: 'Enter basic information' },
    descriptionLabel: { id: 'clu.cluBasicInfoScreen.descriptionLabel', defaultMessage: 'Description' },
    namePlaceholder: { id: 'clu.cluBasicInfoScreen.namePlaceholder', defaultMessage: 'Type project name' },
    azureResourceLabel: { id: 'clu.cluBasicInfoScreen.azureResourceLabel', defaultMessage: 'Azure resource' },
    languageLabel: { id: 'clu.cluBasicInfoScreen.languageLabel', defaultMessage: 'Utterances primary language' },
    changeAzureResource: { id: 'clu.cluBasicInfoScreen.changeAzureResource', defaultMessage: 'Change Azure resource' },
    azureResourcePlaceholder: { id: 'clu.cluBasicInfoScreen.azureResourcePlaceholder', defaultMessage: 'No item selected' },
    multiLingualText: { id: 'clu.cluBasicInfoScreen.multiLingualText', defaultMessage: 'Yes, enable multi-lingual dataset' },
    descriptionPlaceholder: { id: 'clu.cluBasicInfoScreen.descriptionPlaceholder', defaultMessage: 'Type project description' },
    multiLingualLabel: { id: 'clu.cluBasicInfoScreen.multiLingualLabel', defaultMessage: 'Enable multiple languages in project?' },
    languageLabelPlaceholder: { id: 'clu.cluBasicInfoScreen.languageLabelPlaceholder', defaultMessage: 'Select the language of the text' },
    duplicatedNameErrorMessage: { id: 'clu.cluBasicInfoScreen.duplicatedNameErrorMessage', defaultMessage: 'Project name already exists.' },
    description: {
        id: 'clu.cluBasicInfoScreen.description',
        defaultMessage: `Enter the basic information for your conversation project
 such as name and description and select the language which your utterances are in.`
    },
    languageTooltipText: {
        id: 'clu.cluBasicInfoScreen.languageTooltipText',
        defaultMessage: `This is the language of your utterances. If you have multi-lingual
 dataset enabled, this will be the default language unless you select a different language.`
    },
    multiLingualTooltipText: {
        id: 'clu.cluBasicInfoScreen.multiLingualTooltipText',
        defaultMessage: `Enable this option if your utterances are in more than one language.`
    },
    learnMore: {
        id: 'clu.cluBasicInfoScreen.learnMore',
        defaultMessage: 'Learn more about multi-lingual datasets.'
    }
});

const { styleComponent } = getStylistV2('ProjectCreationModalBasicInfo');
const StyledDropdown = styleComponent<LuisDropdownProps>(LuisDropdown)('StyledDropdown', { width: '300px' });
const StyledTextField = styleComponent<LuisTextFieldProps>(LuisTextField)('StyledTextField', { width: '300px' });

export const CluBasicInfoScreen = observer((props: CluBasicInfoScreenProps) => {
    const { projectDto, onUpdate, onNext, onDismiss } = props;

    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();
    const { isLargeOrGreater } = useResponsiveMode();
    const { csAccountStore } = rootStore.azureStore.session;
    const { cluConversationCultureStoreHouse, cluOrchestrationCultureStoreHouse, cluProjectStoreHouse } = rootStore;

    const isAuthoringResourceSelected = !!csAccountStore;
    const isMultiLingualSupported = projectDto.fields.projectType.value !== 'Orchestration';
    const isNameDuplicated = cluProjectStoreHouse.findById(projectDto.fields.name.value) !== undefined;
    const languageOptions: IDropdownOption[] = (projectDto.fields.projectType.value === 'Conversation'
        ? cluConversationCultureStoreHouse
        : cluOrchestrationCultureStoreHouse
    ).items.map(l => ({ key: l.code, text: l.name }));
    const authoringResourcesOptions: IDropdownOption[] = (csAccountStore && [{ key: csAccountStore.id, text: csAccountStore.name }]) || [];

    const onRenderLabel = (label: string, tooltipContent: string | React.ReactNode, isRequired: boolean = false) => (
        <Label required={isRequired}>
            {label}
            <CalloutButton isDarkMode={false} content={tooltipContent} />
        </Label>
    );

    return (
        <>
            <CluModalScreen tokens={{ childrenGap: 30 }}>
                <Stack tokens={{ childrenGap: 6 }}>
                    <LuisText as="h2" variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                        {localize(messages.title)}
                    </LuisText>

                    <LuisText>
                        {localize(messages.description)}&nbsp;
                        <ExternalLink href={multiLingualLearnMore} aria-label={localize(messages.learnMore)}>
                            {localize(globalMessages.learnMore)}
                        </ExternalLink>
                    </LuisText>
                </Stack>

                <Stack tokens={{ childrenGap: 15 }}>
                    <Stack.Item>
                        <StyledDropdown
                            disabled
                            selectedKey={csAccountStore?.id}
                            options={authoringResourcesOptions}
                            label={localize(messages.azureResourceLabel)}
                            placeholder={localize(messages.azureResourcePlaceholder)}
                            styles={!isLargeOrGreater ? { dropdown: { width: 160 } } : undefined}
                        />

                        <LuisText variant="small">
                            <Link onClick={() => dispatcher.dispatch('updateCsAccountSelectModalVisibility', { isVisible: true })}>
                                {localize(messages.changeAzureResource)}
                            </Link>
                        </LuisText>
                    </Stack.Item>

                    <StyledTextField
                        required
                        data-automation-id="projectName"
                        label={localize(messages.nameLabel)}
                        value={projectDto.fields.name.value}
                        disabled={!isAuthoringResourceSelected}
                        placeholder={localize(messages.namePlaceholder)}
                        styles={!isLargeOrGreater ? { fieldGroup: { width: 160 } } : undefined}
                        onChange={(_, projectName) => onUpdate({ name: projectName })}
                        errorMessage={
                            isNameDuplicated ? localize(messages.duplicatedNameErrorMessage) : projectDto.fields.name.errorMessage
                        }
                    />

                    <StyledDropdown
                        options={languageOptions}
                        ariaLabel={localize(messages.languageLabel)}
                        selectedKey={projectDto.fields.culture.value}
                        placeholder={localize(messages.languageLabelPlaceholder)}
                        onChange={(_, o) => onUpdate({ culture: o.key as string })}
                        styles={!isLargeOrGreater ? { dropdown: { width: 160 } } : undefined}
                        onRenderLabel={() => onRenderLabel(localize(messages.languageLabel), localize(messages.languageTooltipText), true)}
                        isLoading={
                            cluConversationCultureStoreHouse.tracker.isRunning || cluOrchestrationCultureStoreHouse.tracker.isRunning
                        }
                    />

                    {isMultiLingualSupported && (
                        <Checkbox
                            label={localize(messages.multiLingualText)}
                            checked={projectDto.fields.isMultiLingual.value}
                            onChange={() => onUpdate({ isMultiLingual: !projectDto.fields.isMultiLingual.value })}
                            onRenderLabel={() =>
                                onRenderLabel(localize(messages.multiLingualLabel), localize(messages.multiLingualTooltipText))
                            }
                        />
                    )}

                    <StyledTextField
                        data-automation-id="projectDescription"
                        disabled={!isAuthoringResourceSelected}
                        label={localize(messages.descriptionLabel)}
                        value={projectDto.fields.description.value}
                        onChange={(_, val) => onUpdate({ description: val })}
                        placeholder={localize(messages.descriptionPlaceholder)}
                        styles={!isLargeOrGreater ? { fieldGroup: { width: 160 } } : undefined}
                    />
                </Stack>
            </CluModalScreen>

            <CluModalFooter
                onNext={onNext}
                onDismiss={onDismiss}
                activeStep={CluProjectCreationSteps.EnterBasicInformation}
                isValid={!isNameDuplicated && projectDto.form.valid && isAuthoringResourceSelected}
                isLoading={cluConversationCultureStoreHouse.tracker.isRunning || cluOrchestrationCultureStoreHouse.tracker.isRunning}
                nextButtonAutomationId="nextButton"
            />
        </>
    );
});
